import { isEmpty } from 'bold-ui'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { emptyArray, partition } from 'util/array'

import {
  ConsultaSeguimentoAplicacaoModel,
  ConsultaSeguimentoAplicacaoStatsModel,
  ConsultaSeguimentoPerguntaStatusEnum,
  PassouFalhouEnum,
} from '../model-consultaSeguimento'

/**
 * Cria a aplicação com status inicial para as perguntas selecionadas
 */
export function createInitialPerguntasStatus(
  perguntasSelecionadas: Readonly<MChatPerguntaEnum[]> = emptyArray
): ConsultaSeguimentoAplicacaoModel {
  if (isEmpty(perguntasSelecionadas)) return {}

  return Object.fromEntries(
    perguntasSelecionadas.map((pergunta) => [pergunta, { status: ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL }])
  )
}

/**
 * Inicializa a primeira pergunta com status RESPONDENDO,
 * ou se já houver dado com status RESPONDENDO_FALHOU ou RESPONDENDO_PASSOU
 */
export function initializeFirstPerguntaStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): ConsultaSeguimentoAplicacaoModel {
  if (isEmpty(aplicacao)) return {}

  const perguntas = Object.keys(aplicacao) as MChatPerguntaEnum[]
  const firstPergunta = perguntas[0]

  if (!firstPergunta) return aplicacao

  const firstPerguntaData = aplicacao[firstPergunta]

  const updated = Object.fromEntries(
    perguntas.map((pergunta) => {
      const item = aplicacao[pergunta]
      const isStatusRespondendoOuDisponivel =
        item.status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO ||
        item.status === ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL

      const newStatus = isStatusRespondendoOuDisponivel
        ? ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL
        : item.status

      return [pergunta, { ...item, status: newStatus }]
    })
  )

  const isFirstPerguntaResponding = isPerguntaStatusRespondendo(firstPerguntaData.status)

  updated[firstPergunta] = {
    ...updated[firstPergunta],
    status: isFirstPerguntaResponding ? firstPerguntaData.status : ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
  }

  return updated
}

/**
 * Inicializa valores da aplicação mesclando perguntas antigas e novas
 */
export function initializeAplicacaoValues(
  perguntasSelecionadas: Readonly<MChatPerguntaEnum[]> = emptyArray,
  previousAplicacao: ConsultaSeguimentoAplicacaoModel = {}
): ConsultaSeguimentoAplicacaoModel {
  const sortedPerguntas = [...perguntasSelecionadas].sort((a, b) => a.localeCompare(b))

  if (isEmpty(previousAplicacao)) {
    const initialStatus = createInitialPerguntasStatus(sortedPerguntas)
    return initializeFirstPerguntaStatus(initialStatus)
  }

  const existingPerguntas = new Set(Object.keys(previousAplicacao))
  const newPerguntas = sortedPerguntas.filter((p) => !existingPerguntas.has(p))
  const newAplicacao = createInitialPerguntasStatus(newPerguntas)

  const mergedAplicacao = Object.fromEntries(
    sortedPerguntas.map((pergunta) => [pergunta, previousAplicacao[pergunta] ?? newAplicacao[pergunta]])
  )

  return initializeFirstPerguntaStatus(mergedAplicacao)
}

/**
 * Verifica se o status é alguma variante de RESPONDENDO
 */
export function isPerguntaStatusRespondendo(status: ConsultaSeguimentoPerguntaStatusEnum): boolean {
  return [
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO,
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU,
    ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU,
  ].includes(status)
}

/**
 * Verifica se uma pergunta específica possui respostas (dados além de status e resultado)
 */
export function hasPerguntaData(pergunta: MChatPerguntaEnum, aplicacao: ConsultaSeguimentoAplicacaoModel): boolean {
  const perguntaData = aplicacao[pergunta]
  if (!perguntaData) return false

  const { status, resultado, ...otherValues } = perguntaData
  return !isEmpty(otherValues)
}

/**
 * Verifica se alguma pergunta na aplicação possui respostas (dados além de status e resultado)
 */
export function hasAnyPerguntaData(aplicacao: ConsultaSeguimentoAplicacaoModel): boolean {
  if (isEmpty(aplicacao)) return false

  return Object.keys(aplicacao).some((item: MChatPerguntaEnum) => {
    return hasPerguntaData(item, aplicacao)
  })
}

/**
 * Retorna informações sobre a pergunta ativa (com status RESPONDENDO, RESPONDENDO_FALHOU ou RESPONDENDO_PASSOU)
 */
export function getPerguntaAtivaInfo(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): {
  perguntaAtiva: MChatPerguntaEnum | null
  index: number
  isFirst: boolean
  isLast: boolean
} | null {
  const perguntas: MChatPerguntaEnum[] = Object.keys(aplicacao ?? {}) as MChatPerguntaEnum[]
  const perguntaAtiva = perguntas.find((pergunta) => isPerguntaStatusRespondendo(aplicacao[pergunta].status))
  const index = perguntas.indexOf(perguntaAtiva)
  const isFirst = index === 0
  const isLast = index === perguntas.length - 1

  return { perguntaAtiva, index, isFirst, isLast }
}

/**
 * Retorna o próximo status da pergunta na aplicação
 */
export function getNextPerguntaStatus(
  status: ConsultaSeguimentoPerguntaStatusEnum
): ConsultaSeguimentoPerguntaStatusEnum | null {
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU
  }
  return null
}

/**
 * Retorna o status anterior da pergunta na aplicação
 */
export function getPreviousPerguntaStatus(
  status: ConsultaSeguimentoPerguntaStatusEnum
): ConsultaSeguimentoPerguntaStatusEnum {
  if (
    status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO ||
    status === ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL
  ) {
    return ConsultaSeguimentoPerguntaStatusEnum.DISPONIVEL
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_FALHOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_FALHOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU
  }
  if (status === ConsultaSeguimentoPerguntaStatusEnum.FINALIZADO_PASSOU) {
    return ConsultaSeguimentoPerguntaStatusEnum.RESPONDENDO_PASSOU
  }
  return ConsultaSeguimentoPerguntaStatusEnum.NAO_DISPONIVEL
}

/**
 * Atualiza a última pergunta para o status anterior
 */
export function setLastPerguntaToPreviousStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): ConsultaSeguimentoAplicacaoModel {
  const perguntas = Object.keys(aplicacao) as MChatPerguntaEnum[]
  const lastPergunta = perguntas[perguntas.length - 1]

  if (!lastPergunta) return aplicacao

  return {
    ...aplicacao,
    [lastPergunta]: {
      ...aplicacao[lastPergunta],
      status: getPreviousPerguntaStatus(aplicacao[lastPergunta].status),
    },
  }
}

/**
 * Atualiza a última pergunta para o próximo status
 */
export function setLastPerguntaToNextStatus(
  aplicacao: ConsultaSeguimentoAplicacaoModel
): ConsultaSeguimentoAplicacaoModel {
  const perguntas = Object.keys(aplicacao) as MChatPerguntaEnum[]
  const lastPergunta = perguntas[perguntas.length - 1]

  if (!lastPergunta) return aplicacao

  const nextStatus = getNextPerguntaStatus(aplicacao[lastPergunta].status)
  if (!nextStatus) return aplicacao

  return {
    ...aplicacao,
    [lastPergunta]: {
      ...aplicacao[lastPergunta],
      status: nextStatus,
    },
  }
}

/**
 * Retorna lista de perguntas que possuem dados além de status e resultado
 */
export function getPerguntasWithData(aplicacao: ConsultaSeguimentoAplicacaoModel): MChatPerguntaEnum[] {
  if (isEmpty(aplicacao)) return []

  return Object.keys(aplicacao).filter((item: MChatPerguntaEnum) => {
    return hasPerguntaData(item, aplicacao)
  }) as MChatPerguntaEnum[]
}

export function getAplicacaoStats(aplicacao: ConsultaSeguimentoAplicacaoModel): ConsultaSeguimentoAplicacaoStatsModel {
  if (isEmpty(aplicacao)) return null

  const perguntasRespondidas = Object.keys(aplicacao) as MChatPerguntaEnum[]

  const [perguntasPassou, perguntasFalhou] = partition(
    perguntasRespondidas,
    (pergunta) => aplicacao[pergunta].resultado === PassouFalhouEnum.PASSOU
  )

  const totalOfPerguntasRespondidas = perguntasRespondidas.length
  const numberOfPerguntasPassou = perguntasPassou.length
  const numberOfPerguntasFalhou = perguntasFalhou.length

  return {
    numberOfPerguntasFalhou,
    numberOfPerguntasPassou,
    totalOfPerguntasRespondidas,
  }
}
